import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { AppointmentService, IAppointment } from '@shared/services/appointment.service';
import { CalendarService, ICalendar, IResponseCalendar } from '@shared/services/calendar.service';
import { NotificationService } from '@shared/services/notification.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { Subject, takeUntil } from 'rxjs';
import { GenericCalendarComponent } from '../../shared/components/generic-calendar/generic-calendar.component';
import { CreateAppointmentComponent, IDialogData } from './create-appointment/create-appointment.component';
import { CreateCalendarComponent } from './create-calendar/create-calendar.component';
import { CreateHolidaysComponent } from './create-holidays/create-holidays.component';
import { DeleteCalendarComponent } from './delete-calendar/delete-calendar.component';
import { UpdateAppointmentComponent } from './update-appointment/update-appointment.component';

const MONTHS = [
	{ no: 0, name: 'january' },
	{ no: 1, name: 'february' },
	{ no: 2, name: 'march' },
	{ no: 3, name: 'april' },
	{ no: 4, name: 'may' },
	{ no: 5, name: 'june' },
	{ no: 6, name: 'july' },
	{ no: 7, name: 'august' },
	{ no: 8, name: 'september' },
	{ no: 9, name: 'october' },
	{ no: 10, name: 'november' },
	{ no: 11, name: 'december' },
];

@Component({
	selector: 'app-calendar-component',
	templateUrl: './calendar.component.html',
	styleUrls: ['./calendar.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CalendarComponent implements OnInit {
	public readonly DEFAULT_CALENDAR = '00000000-0000-0000-0000-000000000000';

	private destroy$: Subject<void> = new Subject<void>();
	public isLoading: boolean = false;
	public selectedMonth: number;
	public selectedYear: number;
	public selectedCalendar: ICalendar;
	public calendars: ICalendar[];
	public workTasks: IWorktask[];
	public appointments: IAppointment[];
	public months = [];
	public years = [];

	@ViewChild(GenericCalendarComponent) calendar: GenericCalendarComponent;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_upload',
			text: () => 'mes.administration.calendar.create-year-btn',
			click: () => this.openCreateHolidays(),
		},
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.calendar.create-calendar.create-calendar-btn',
			click: () => this.openCreateCalendar(),
		},
		{
			icon: () => 'acies_delete',
			text: () => 'mes.administration.calendar.delete-calendar.heading',
			click: () => this.openCreateHolidays(),
			disabled: () => !this.selectedCalendar || this.selectedCalendar.id === this.DEFAULT_CALENDAR,
			hideOnDisable: () => true,
		},
	];

	constructor(
		public dialog: MatDialog,
		public dialogYear: MatDialog,
		public dialogDay: MatDialog,
		public calendarService: CalendarService,
		public appointmentService: AppointmentService,
		public worktaskService: WorktaskService,
		private notificationService: NotificationService,
		private translator: TranslateService,
	) {}

	public ngOnInit(): void {
		this.worktaskService.getWorktasks().subscribe((response) => {
			this.workTasks = response.result;
		});

		this.getCalendars();

		this.months = MONTHS;

		this.selectedYear = new Date().getFullYear();
		this.selectedMonth = new Date().getMonth();
		for (let year = this.selectedYear + 10; year >= 2020; year--) {
			this.years.push(year);
		}
	}

	public onSelected(calendar, year, month): void {
		this.selectedMonth = Number.parseInt(month);
		this.selectedYear = Number.parseInt(year);
		this.selectedCalendar = calendar;
		this.getAppointments().subscribe((c) => {
			this.appointments = c.result;
		});
	}

	public loadData() {
		this.isLoading = true;

		this.getCalendars();

		this.isLoading = false;
	}

	private getCalendars() {
		this.calendarService.getCalendars().subscribe((calendars) => {
			this.calendars = calendars.result;

			this.selectedCalendar = this.calendars.find((calendar) => calendar.id === this.DEFAULT_CALENDAR);

			this.getAppointments().subscribe((c) => {
				this.appointments = c.result;
			});
		});
	}

	private getAppointments() {
		const year = this.selectedYear;
		const month = this.selectedMonth;

		// First day of the current month
		const firstDayOfMonth = new Date(year, month, 1);

		// Last day of the current month
		const lastDayOfMonth = new Date(year, month + 1, 0);

		// Calculate the Monday of the week containing the 1st of the month
		const firstVisibleDay = new Date(firstDayOfMonth);
		const dayOfWeekForFirst = firstVisibleDay.getDay(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday
		if (dayOfWeekForFirst !== 1) {
			// If it's not Monday, adjust to the previous Monday
			firstVisibleDay.setDate(firstVisibleDay.getDate() - ((dayOfWeekForFirst + 6) % 7));
		}

		// Calculate the Sunday of the week containing the last day of the month
		const lastVisibleDay = new Date(lastDayOfMonth);
		const dayOfWeekForLast = lastVisibleDay.getDay();
		if (dayOfWeekForLast !== 0) {
			// If it's not Sunday, adjust to the next Sunday
			lastVisibleDay.setDate(lastVisibleDay.getDate() + (7 - dayOfWeekForLast));
		}

		// Format the dates as YYYY-MM-DD strings
		const formattedFirstDay = `${firstVisibleDay.getFullYear()}-${(firstVisibleDay.getMonth() + 1).toString().padStart(2, '0')}-${firstVisibleDay.getDate().toString().padStart(2, '0')}`;
		const formattedLastDay = `${lastVisibleDay.getFullYear()}-${(lastVisibleDay.getMonth() + 1).toString().padStart(2, '0')}-${lastVisibleDay.getDate().toString().padStart(2, '0')}`;

		// Fetch appointments between the first and last visible days
		return this.appointmentService.getAppointmentsBetweenDates(this.selectedCalendar.id, formattedFirstDay, formattedLastDay);
	}

	public openCreateCalendar(calendarData: object = null) {
		const dialogRef = this.dialog.open(CreateCalendarComponent, {
			disableClose: true,
			width: '650px',
			data: calendarData,
		});

		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe((newCalendar: IResponseCalendar) => {
			if (newCalendar) {
				this.calendars.push(newCalendar.result);
				this.selectedCalendar = newCalendar.result;
				this.onSelected(this.selectedCalendar, this.selectedYear, this.selectedMonth);

				this.notificationService.notify(['mes.administration.calendar.create-calendar.notifications.header'], ['mes.administration.calendar.create-calendar.notifications.content', { alias: newCalendar.result.alias }], 'success');
			}
		});
	}

	public openDeleteCalendar() {
		const dialogRef = this.dialog.open(DeleteCalendarComponent, {
			disableClose: true,
			width: '450px',
			data: this.selectedCalendar,
		});

		dialogRef.componentInstance.deleted.pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.notificationService.notify(['mes.administration.calendar.delete-calendar.notifications.success.header'], ['mes.administration.calendar.delete-calendar.notifications.success.content', { alias: this.selectedCalendar.alias }], 'success');
				this.getCalendars();
			},
			error: (err) => {
				const errorMessage = err.error?.error?.message;

				if (errorMessage && errorMessage.includes('currently in use')) {
					this.notificationService.notify(['mes.administration.calendar.delete-calendar.notifications.error.in-use.header'], ['mes.administration.calendar.delete-calendar.notifications.error.in-use.content', { alias: this.selectedCalendar.alias }], 'error');
				} else {
					this.notificationService.notify(['mes.administration.calendar.delete-calendar.notifications.error.bad-request.header'], ['mes.administration.calendar.delete-calendar.notifications.error.bad-request.content', { alias: this.selectedCalendar.alias }], 'error');
				}
			},
		});
	}

	public openCreateHolidays() {
		const dialogRef = this.dialog.open(CreateHolidaysComponent, {
			width: '450px',
			data: {
				workTasks: this.workTasks,
				calendarId: this.selectedCalendar.id,
			},
		});

		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe({
			next: () => {
				this.notificationService.notify(['mes.administration.calendar.create-holidays.notifications.success.header'], ['mes.administration.calendar.create-holidays.notifications.success.content'], 'success');

				this.getAppointments().subscribe((c) => {
					this.appointments = c.result;
				});
			},
		});
	}

	public openCreateAppointment(appointment: IAppointment): void {
		const data: IDialogData = {
			appointment: {
				...appointment,
				calendarId: this.selectedCalendar.id,
			},
			workTasks: this.workTasks,
		};

		const dialogRef = this.dialog.open(CreateAppointmentComponent, {
			disableClose: true,
			width: '450px',
			data,
		});

		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.notificationService.notify(['mes.administration.appointments.notifications.created.header'], ['mes.administration.appointments.notifications.created.content'], 'success');
			this.getAppointments().subscribe((c) => {
				this.appointments = c.result;
			});
		});
	}

	public openUpdateAppointment(appointment: IAppointment): void {
		const data: IDialogData = {
			appointment: {
				...appointment,
				calendarId: this.selectedCalendar.id,
			},
			workTasks: this.workTasks,
		};

		const dialogRef = this.dialog.open(UpdateAppointmentComponent, {
			disableClose: true,
			width: '450px',
			data,
		});

		dialogRef.componentInstance.updated.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.notificationService.notify(['mes.administration.appointments.notifications.updated.header'], ['mes.administration.appointments.notifications.updated.content'], 'success');
			this.getAppointments().subscribe((c) => {
				this.appointments = c.result;
			});
		});
	}

	public dayClicked(date: Date) {
		// Convert date to ISO string
		const startTimeString = date.toISOString();

		const appointment: IAppointment = {
			id: null,
			startTime: startTimeString,
			endTime: startTimeString,
			title: null,
			appointmentType: null,
			calendarId: null,
			workTaskId: null,
		};

		this.openCreateAppointment(appointment);
	}

	public appointmentClicked(appointment: IAppointment) {
		this.openUpdateAppointment(appointment);
	}

	public appointmentDeleteClicked(appointment: IAppointment) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('mes.administration.calendar.delete-appointment.header'),
				content: this.translator.instant('mes.administration.calendar.delete-appointment.content', {
					title: appointment.title,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					this.deleteAppointment(appointment);
				}
			});
	}

	public deleteAppointment(appointment: IAppointment) {
		this.appointmentService.deleteAppointmentById(appointment.calendarId, appointment.id).subscribe({
			next: () => {
				this.notificationService.notify(['mes.administration.calendar.delete-appointment.notifications.header'], ['mes.administration.calendar.delete-appointment.notifications.content', { title: appointment.title }], 'success');
				this.getAppointments().subscribe((c) => {
					this.appointments = c.result;
				});
			},
		});
	}
}
